import { api, bytesText, download, sendFile } from '../console/api.ts';
import { carrierName, planName } from '../console/carriers.ts';
import { empty, field, meter, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { esc, icon, person, when } from '../console/format.ts';
import { type Actions, type ClickAction, current, render } from '../console/router.ts';
import { me, supervises } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import { checkboxControl, textControl } from '../controls.ts';
import { fileButton } from '../file-picker.ts';
import {
  holderDraft,
  holderInput,
  keepHolderDraft,
  type QuoteReq,
  sellingAgent,
  setQuoting,
  type UwFile,
  uwFileId,
} from './cotizar.ts';

/**
 * «Expediente de suscripción»: what the carrier asks before issuing (documents, the health
 * declaration and its signature, the vehicle's photos, the RENAP check), its review and the
 * issuance. The open file is the quote's (Cotizar keeps it and forgets it with the session).
 */

const UW_STAGE: Record<string, [string, string]> = {
  COLLECTING: ['info', 'En preparación'],
  COMPLETE: ['ok', 'Completo'],
  SENT_TO_CARRIER: ['warn', 'En revisión de la aseguradora'],
  ACCEPTED: ['ok', 'Aceptado por la aseguradora'],
  DECLINED: ['bad', 'Declinado por la aseguradora'],
};
const UW_DOC: Record<string, [string, string]> = {
  PENDING: ['warn', 'Falta'],
  UPLOADED: ['info', 'Por revisar'],
  ACCEPTED: ['ok', 'Aceptado'],
  REJECTED: ['bad', 'Rechazado'],
};
const UW_SLOT: Record<string, string> = {
  FRONT: 'Frente',
  BACK: 'Atrás',
  LEFT: 'Lado izquierdo',
  RIGHT: 'Lado derecho',
  ODOMETER: 'Odómetro',
  VIN: 'Número VIN',
};
const UW_CONDITION: [string, string][] = [
  ['DIABETES', 'Diabetes'],
  ['HYPERTENSION', 'Hipertensión'],
  ['CANCER', 'Cáncer'],
  ['HEART_DISEASE', 'Enfermedad del corazón'],
  ['PREGNANCY', 'Embarazo'],
];
const UW_FIELD: Record<string, string> = {
  conditions: 'padecimientos',
  hospitalizedLast5Years: 'hospitalización',
  smoker: 'si fuma',
};

/** Downloads a document of the file (the API records who and when) without caching it. */
function downloadDocument(fileId: string, documentId: string) {
  return download(
    `/v1/underwriting-files/${encodeURIComponent(fileId)}/documents/${encodeURIComponent(documentId)}/content`,
  );
}

const uwPath = (rest = '') => `/v1/underwriting-files/${encodeURIComponent(uwFileId ?? '')}${rest}`;

export async function expediente() {
  const back =
    '<button type="button" class="btn" data-go="cotizar">Volver a la cotización</button>';
  if (!uwFileId)
    return (
      pageHead('Expediente de suscripción', 'Lo que la aseguradora pide antes de emitir.', back) +
      empty(
        'policy',
        'Ningún expediente abierto',
        'Abre una cotización y, en la opción que eligió el cliente, toca «Preparar emisión».',
        back,
        2,
      )
    );
  const f = await api<UwFile>('GET', uwPath());
  const [pillCls, stageText] = UW_STAGE[f.stage] ?? ['info', f.stage];
  const manage = supervises();
  // Habeas Data: who read the health declaration or downloaded a document (management).
  const log = manage ? await api<UwAccess[]>('GET', uwPath('/access-log')).catch(() => null) : null;
  const locked = ['SENT_TO_CARRIER', 'ACCEPTED', 'DECLINED'].includes(f.stage);
  const why =
    f.requiredBecause === 'REFER'
      ? `${carrierName(f.carrier)} dejó el riesgo en revisión: su suscriptor lo resuelve con este expediente.`
      : f.requiredBecause === 'CARRIER_REQUIRES_DOCUMENTS'
        ? `${carrierName(f.carrier)} pide estos requisitos antes de emitir.`
        : 'Opcional: la aseguradora acepta recibir estos requisitos después de emitir.';
  const ratio = f.progress.total ? f.progress.done / f.progress.total : 0;
  const summary = `<section class="card uw-summary"><div class="card-head">${icon('shield')}<h2>Resumen</h2><span class="spacer"></span>${pill(pillCls, stageText)}</div>
    <div class="uw-progress"><div><span class="kpi-value">${f.progress.done} de ${f.progress.total}</span><span class="muted small"> requisitos listos</span></div>${meter(ratio)}</div>
    <p class="muted small uw-why">${esc(why)}</p>
    ${
      f.missing.length
        ? `<ul class="uw-missing" aria-label="Lo que falta">${f.missing.map((m) => `<li>${icon('alert')}<span>${esc(m.text)}</span></li>`).join('')}</ul>`
        : `<p class="uw-ready">${icon('ok')}<span>Todo listo para emitir.</span></p>`
    }</section>`;

  const docs = f.documents.length
    ? `<section class="card"><div class="card-head">${icon('policy')}<h2>Documentos</h2><span class="spacer"></span><span class="muted small">PDF o foto, hasta 5 MB</span></div>
      <ul class="uw-list">${f.documents
        .map((d) => {
          const [cls, label] = UW_DOC[d.status] ?? ['info', d.status];
          const input = `uw-doc-${d.code}`;
          const upload =
            locked || d.status === 'ACCEPTED'
              ? ''
              : fileButton({
                  id: input,
                  label: `${icon('upload')}${d.upload ? 'Subir otro' : 'Subir'}`,
                  accept: 'image/*,application/pdf',
                  attrs: `data-uw-doc="${esc(d.code)}"`,
                });
          const review =
            manage && !locked && d.status === 'UPLOADED'
              ? `<div class="uw-review">${textControl({ label: `Motivo del rechazo de ${d.label}`, hideLabel: true, 'data-uw-reason': d.code, placeholder: 'Motivo si lo rechazas', maxlength: 500 })}
                 <button type="button" class="btn sm danger" data-uw-reject="${esc(d.code)}">Rechazar</button>
                 <button type="button" class="btn sm primary" data-uw-accept="${esc(d.code)}">Aceptar</button></div>`
              : '';
          return `<li class="uw-item"><div class="uw-item-main"><strong>${esc(d.label)}</strong>
              ${d.source === 'CARRIER' ? '<span class="tag">Lo pide la aseguradora</span>' : ''}
              <div class="muted small">${
                d.upload
                  ? `<button type="button" class="link" data-uw-download="${esc(d.upload.documentId)}">${esc(d.upload.fileName)}</button> · ${bytesText(d.upload.size)} · ${esc(when(d.upload.at))}`
                  : 'Sin archivo'
              }${d.status === 'REJECTED' && d.review?.reason ? ` · <span class="bad-text">Rechazado: ${esc(d.review.reason)}</span>` : ''}</div></div>
            ${pill(cls, label)}<div class="uw-actions">${upload}</div>${review}</li>`;
        })
        .join('')}</ul></section>`
    : '';

  const decl = f.declaration;
  const declaration = decl
    ? `<section class="card" id="uw-declaration"><div class="card-head">${icon('users')}<h2>Declaración de salud</h2><span class="spacer"></span>
        ${decl.signature ? pill('ok', 'Firmada') : decl.inconsistencies.length ? pill('bad', 'No coincide con lo cotizado') : pill('warn', 'Sin firmar')}</div>
      ${
        decl.inconsistencies.length
          ? `<p class="note bad">${icon('alert')}<span>No coincide con lo que se cotizó: ${esc(
              decl.inconsistencies
                .map(
                  (i) =>
                    `${decl.insured.find((p) => p.personId === i.personId)?.label ?? i.personId} (${UW_FIELD[i.field] ?? i.field})`,
                )
                .join(', '),
            )}. Corrige la respuesta o recotiza con los datos correctos.</span></p>`
          : ''
      }
      <div class="uw-people">${decl.insured
        .map((p) => {
          const a = decl.answers.find((x) => x.personId === p.personId);
          const dis = !!(decl.signature || locked);
          return `<fieldset data-uw-person="${esc(p.personId)}"><legend>${esc(p.label)}</legend>
            <p class="muted small">Cotizado: ${esc(
              [
                p.asked.conditions
                  ? p.asked.conditions.length
                    ? p.asked.conditions
                        .map((c) => UW_CONDITION.find((x) => x[0] === c)?.[1] ?? c)
                        .join(', ')
                    : 'sin padecimientos'
                  : '',
                p.asked.hospitalizedLast5Years === undefined
                  ? ''
                  : p.asked.hospitalizedLast5Years
                    ? 'con hospitalización'
                    : 'sin hospitalización',
                p.asked.smoker === undefined ? '' : p.asked.smoker ? 'fuma' : 'no fuma',
              ]
                .filter(Boolean)
                .join(' · ') || '—',
            )}</p>
            <div class="checks" role="group" aria-label="Padecimientos de ${esc(p.label)}">${UW_CONDITION.map(
              ([code, label]) =>
                checkboxControl(
                  {
                    class: 'check',
                    'data-uw-cond': code,
                    checked: !!a?.conditions.includes(code),
                    disabled: dis,
                  },
                  esc(label),
                ),
            ).join('')}</div>
            ${checkboxControl({ class: 'check', id: `uw-hosp-${p.personId}`, 'data-uw-hosp': true, checked: !!a?.hospitalizedLast5Years, disabled: dis }, 'Estuvo hospitalizado en los últimos 5 años')}
            ${checkboxControl({ class: 'check', 'data-uw-smoker': true, checked: !!a?.smoker, disabled: dis }, 'Fuma')}
            ${checkboxControl({ class: 'check', 'data-uw-treat': true, checked: !!a?.underTreatment, disabled: dis }, 'Está en tratamiento o toma medicamentos')}
            ${field('Cuál tratamiento', textControl({ 'data-uw-treat-detail': true, maxlength: 500, value: a?.treatmentDetail ?? '', disabled: dis }))}
          </fieldset>`;
        })
        .join('')}</div>
      ${
        decl.signature
          ? `<p class="uw-ready">${icon('ok')}<span>Firmada por <strong>${esc(decl.signature.signerName)}</strong> el ${esc(when(decl.signature.at))}.</span></p>`
          : locked
            ? ''
            : `<div class="form-foot"><button type="button" class="btn" data-uw-declare>Guardar respuestas</button></div>
      <div class="uw-sign"><p class="consent">${esc(decl.consentText)}</p>
        ${field('Nombre de quien firma', textControl({ id: 'uw-signer', maxlength: 120, autocomplete: 'off', value: holderDraft?.fullName ?? '' }))}
        ${checkboxControl({ class: 'check', id: 'uw-consent' }, 'Leí y acepto la declaración')}
        <button type="button" class="btn primary" data-uw-sign ${decl.answers.length ? '' : 'disabled'}>Firmar declaración</button></div>`
      }</section>`
    : '';

  const insp = f.inspection;
  const inspection = insp
    ? `<section class="card"><div class="card-head">${icon('camera')}<h2>Inspección del vehículo</h2><span class="spacer"></span>
        <span class="muted small">${insp.slots.filter((s) => s.photo).length} de ${insp.slots.length} fotos</span></div>
      <p class="muted small uw-why">Toma cada foto con el celular, con buena luz y el vehículo completo en el cuadro.</p>
      <ul class="uw-photos">${insp.slots
        .map((s) => {
          const id = `uw-photo-${s.slot}`;
          return `<li class="uw-photo ${s.photo ? 'done' : ''}">${icon(s.photo ? 'ok' : 'camera')}<div><strong>${esc(UW_SLOT[s.slot] ?? s.slot)}</strong>
            <div class="muted small">${s.photo ? `<button type="button" class="link" data-uw-download="${esc(s.photo.documentId)}">Tomada ${esc(when(s.photo.takenAt ?? s.photo.at))}</button>` : 'Falta'}</div></div>
            ${locked ? '' : fileButton({ id, label: s.photo ? 'Repetir' : 'Tomar foto', accept: 'image/*', capture: 'environment', attrs: `data-uw-photo="${esc(s.slot)}"` })}</li>`;
        })
        .join('')}</ul></section>`
    : '';

  const h = holderDraft ?? {};
  const lastCheck = f.biometric.attempts[f.biometric.attempts.length - 1];
  const holder = `<section class="card"><div class="card-head">${icon('lock')}<h2>Contratante y validación RENAP</h2><span class="spacer"></span>
      ${f.biometric.matched ? pill('ok', 'Validado') : lastCheck ? pill('bad', 'No coincide') : pill('warn', 'Sin validar')}</div>
    <form id="holder-form" class="form-grid" novalidate>
      <fieldset><legend>Persona</legend>
        ${field('Nombre completo', textControl({ id: 'h-name', maxlength: 120, autocomplete: 'off', value: h.fullName ?? '' }))}
        ${field('DPI (CUI)', textControl({ id: 'h-cui', inputmode: 'numeric', maxlength: 15, placeholder: '13 dígitos', autocomplete: 'off', value: h.nationalId ?? '' }))}
        ${field('NIT', textControl({ id: 'h-nit', maxlength: 15, placeholder: '1234567-8', autocomplete: 'off', value: h.taxId ?? '' }))}
      </fieldset>
      <fieldset><legend>Contacto</legend>
        ${field('Correo', textControl({ id: 'h-email', type: 'email', autocomplete: 'off', value: h.email ?? '' }))}
        ${field('Teléfono (opcional)', textControl({ id: 'h-phone', inputmode: 'tel', placeholder: '+502 5555 0199', autocomplete: 'off', value: h.phone ?? '' }))}
      </fieldset>
    </form>
    <div class="form-foot"><span class="muted small">${
      lastCheck
        ? `${lastCheck.match ? 'Coincide' : 'No coincide'}: ${esc(lastCheck.fullName)}, DPI terminado en ${esc(lastCheck.dpiLast4)} · ${esc(when(lastCheck.at))}${lastCheck.reason ? ` · ${esc(lastCheck.reason)}` : ''}`
        : 'La aseguradora emite a nombre de la persona que valides aquí.'
    }</span>
    ${f.biometric.matched || locked ? '' : '<button type="button" class="btn" data-uw-biometric>Validar con RENAP</button>'}</div></section>`;

  const r = f.referral;
  const carrier =
    f.decision === 'REFER'
      ? `<section class="card"><div class="card-head">${icon('inbox')}<h2>Revisión de ${esc(carrierName(f.carrier))}</h2></div>
      <p class="uw-why">${
        !r || r.withdrawn
          ? f.complete
            ? 'El expediente está completo: envíalo al suscriptor de la aseguradora.'
            : 'Cuando el expediente esté completo, se envía al suscriptor de la aseguradora como una orden de trabajo.'
          : !r.resolution
            ? `Enviado el ${esc(when(r.sent.at))} en la orden <b class="mono">${esc(r.orderId)}</b>. La administración anota la resolución al completar la orden.`
            : r.resolution.decision === 'ACCEPT'
              ? `Aceptado${r.resolution.surchargePercent ? ` con un recargo de ${esc(r.resolution.surchargePercent)} %` : ''}${r.resolution.exclusions.length ? `; excluye: ${esc(r.resolution.exclusions.join(', '))}` : ''}.`
              : `La aseguradora declinó el riesgo${r.resolution.note ? `: ${esc(r.resolution.note)}` : ''}.`
      }</p>
      ${!r || r.withdrawn ? `<div class="form-foot"><span></span><button type="button" class="btn primary" data-uw-send ${f.complete ? '' : 'disabled'}>Enviar a la aseguradora</button></div>` : ''}</section>`
      : '';

  const issueHint = f.readyToIssue
    ? 'La aseguradora emite a nombre del contratante validado.'
    : f.required
      ? 'Se habilita cuando el expediente esté completo.'
      : 'Puedes emitir; el expediente puede completarse después.';
  const canIssue = f.readyToIssue || !f.required;
  const foot = `<div class="card uw-issue"><span class="muted small" id="uw-issue-hint">${esc(issueHint)}</span>
    <button type="button" class="btn primary lg" data-uw-issue ${canIssue ? '' : 'disabled aria-disabled="true"'} aria-describedby="uw-issue-hint">${icon('shield')}Emitir póliza</button></div>`;

  return `${pageHead(
    'Expediente de suscripción',
    `${esc(f.label)} · ${esc(carrierName(f.carrier))} · plan ${esc(planName(f.carrier, f.option, f.productLine))} <span class="mono muted">${esc(f.id)}</span>`,
    back,
  )}<div class="uw-grid">${summary}${docs}${declaration}${inspection}${holder}${carrier}${log ? accessLogCard(f, log) : ''}${foot}</div>`;
}

type UwAccess = {
  documentId: string | null;
  item: string;
  action: 'DOWNLOAD' | 'READ';
  by: string;
  at: string;
};
/** Who read the health declaration or downloaded a document of the file, newest first. */
function accessLogCard(f: UwFile, log: UwAccess[]) {
  const what = (a: UwAccess) =>
    a.item === 'DECLARATION'
      ? 'Leyó la declaración de salud'
      : a.item.startsWith('PHOTO:')
        ? `Descargó la foto ${UW_SLOT[a.item.slice(6)] ?? a.item.slice(6)}`
        : `Descargó ${f.documents.find((d) => d.code === a.item)?.label ?? a.item}`;
  const rows = [...log].reverse().slice(0, 50);
  return `<section class="card" id="uw-access-log"><div class="card-head">${icon('lock')}<h2>Bitácora de accesos</h2><span class="spacer"></span><span class="muted small">Solo la ve la dirección y la administración</span></div>
    ${
      rows.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Cuándo</th><th>Quién</th><th>Qué</th></tr></thead><tbody>${rows
            .map(
              (a) =>
                `<tr><td class="nowrap">${esc(when(a.at))}</td><td>${esc(person(a.by))}</td><td>${esc(what(a))}</td></tr>`,
            )
            .join(
              '',
            )}</tbody></table></div>${log.length > rows.length ? `<p class="muted small">Se muestran los ${rows.length} accesos más recientes de ${log.length}.</p>` : ''}`
        : '<p class="muted party-none">Nadie ha leído la declaración de salud ni descargado documentos de este expediente.</p>'
    }</section>`;
}

/** The declaration as typed, per insured person. */
function declarationInput() {
  return [...document.querySelectorAll<HTMLElement>('[data-uw-person]')].map((box) => {
    const checked = (sel: string) => box.querySelector<HTMLInputElement>(sel)?.checked ?? false;
    const underTreatment = checked('[data-uw-treat]');
    const detail =
      box.querySelector<HTMLInputElement>('[data-uw-treat-detail]')?.value.trim() ?? '';
    if (underTreatment && !detail)
      throw new InputError('Escribe cuál tratamiento o medicamento toma.');
    return {
      personId: box.dataset.uwPerson as string,
      conditions: [...box.querySelectorAll<HTMLInputElement>('[data-uw-cond]')]
        .filter((i) => i.checked)
        .map((i) => i.dataset.uwCond as string),
      hospitalizedLast5Years: checked('[data-uw-hosp]'),
      smoker: checked('[data-uw-smoker]'),
      underTreatment,
      treatmentDetail: underTreatment ? detail : null,
    };
  });
}

/** A document's review: accepted, or rejected with the reason the agent will read. */
const review: ClickAction = (t, d) => {
  const code = (d.uwAccept ?? d.uwReject) as string;
  const reason =
    document
      .querySelector<HTMLInputElement>(`[data-uw-reason="${CSS.escape(code)}"]`)
      ?.value.trim() ?? '';
  void act(t, 'Guardando la revisión…', async () => {
    if (d.uwReject && !reason)
      throw new InputError(
        'Escribe por qué rechazas el documento, para que la agente sepa qué traer.',
      );
    keepHolderDraft();
    await api('POST', uwPath(`/documents/${encodeURIComponent(code)}/review`), {
      decision: d.uwAccept ? 'ACCEPT' : 'REJECT',
      ...(d.uwReject ? { reason } : {}),
    });
    await render(current, { quiet: true });
    return d.uwAccept ? 'Documento aceptado.' : 'Documento rechazado; la agente verá el motivo.';
  });
};

/** The declaration's answers, saved (Guardar respuestas) or saved and signed (Firmar). */
const declare: ClickAction = (t, d) => {
  const signing = d.uwSign !== undefined;
  void act(t, signing ? 'Firmando…' : 'Guardando la declaración…', async () => {
    keepHolderDraft();
    if (!signing) {
      const f = await api<UwFile>('PUT', uwPath('/declaration'), { answers: declarationInput() });
      await render(current, { quiet: true });
      return f.declaration?.inconsistencies.length
        ? 'Guardada, pero no coincide con lo cotizado: revisa lo señalado.'
        : 'Respuestas guardadas. Falta la firma.';
    }
    const consent = ($('#uw-consent') as HTMLInputElement).checked;
    if (!consent) throw new InputError('Marca que la persona leyó y acepta la declaración.');
    const signerName = textInput('#uw-signer', 'el nombre de quien firma');
    await api('PUT', uwPath('/declaration'), { answers: declarationInput() });
    await api('POST', uwPath('/declaration/signature'), { signerName, consent });
    await render(current, { quiet: true });
    return `Declaración firmada por ${signerName}.`;
  });
};

/** A document or a photo chosen in its file picker goes up right away. */
function upload(up: HTMLInputElement) {
  if (!up.files?.[0] || !uwFileId) return;
  const file = up.files[0];
  const photo = up.dataset.uwPhoto;
  const path = photo
    ? uwPath(`/inspection/${encodeURIComponent(photo)}`)
    : uwPath(`/documents/${encodeURIComponent(up.dataset.uwDoc ?? '')}`);
  const label = up.closest('li')?.querySelector<HTMLElement>('label.file-btn') ?? null;
  void act(label, 'Subiendo…', async () => {
    keepHolderDraft();
    await sendFile(path, file);
    await render(current, { quiet: true });
    return photo
      ? `Foto «${UW_SLOT[photo] ?? photo}» guardada.`
      : `«${file.name}» subido; queda por revisar.`;
  });
}

export const actions: Actions = {
  click: {
    uwAccept: review,
    uwReject: review,
    uwDownload: (t, d) => {
      if (!uwFileId) return;
      const fileId = uwFileId;
      void act(t, 'Descargando…', async () => {
        const name = await downloadDocument(fileId, d.uwDownload ?? '');
        return `«${name}» descargado. La descarga quedó registrada.`;
      });
    },
    uwDeclare: declare,
    uwSign: declare,
    uwBiometric: (t) => {
      void act(t, 'Validando con RENAP…', async () => {
        keepHolderDraft();
        const fullName = textInput('#h-name', 'el nombre del contratante');
        const nationalId = textInput('#h-cui', 'el DPI del contratante').replace(/[\s-]/g, '');
        if (!/^\d{13}$/.test(nationalId)) throw new InputError('El DPI (CUI) lleva 13 dígitos.');
        const f = await api<UwFile>('POST', uwPath('/biometric-check'), { nationalId, fullName });
        await render(current, { quiet: true });
        if (!f.biometric.matched)
          throw new InputError('RENAP no reconoce a la persona con ese DPI.');
        return `${fullName} validado con RENAP.`;
      });
    },
    uwSend: (t) => {
      void act(t, 'Enviando a la aseguradora…', async () => {
        keepHolderDraft();
        const f = await api<UwFile>('POST', uwPath('/send-to-carrier'), {});
        await render(current, { quiet: true });
        return `Expediente enviado a ${carrierName(f.carrier)} en la orden ${f.referral?.orderId ?? ''}.`;
      });
    },
    uwIssue: (t) => {
      if (!uwFileId) return;
      void act(t, 'Emitiendo con la aseguradora…', async () => {
        const policyholder = holderInput();
        keepHolderDraft();
        const f = await api<UwFile>('GET', uwPath());
        const q = await api<{ versions: { number: number; request: QuoteReq }[] }>(
          'GET',
          `/v1/quotes/${encodeURIComponent(f.quoteId)}`,
        );
        const r = await api<{ policyNumber?: string; status?: string; message?: string }>(
          'POST',
          '/v1/policies',
          {
            carrier: f.carrier,
            request: q.versions.find((x) => x.number === f.version)?.request,
            option: f.option,
            agentId: f.agentId ?? me?.agentId ?? sellingAgent(),
            quoteId: f.quoteId,
            version: f.version,
            policyholder,
          },
        );
        setQuoting({ lastQuote: null, uwFileId: null, holderDraft: null });
        // A referred risk the carrier accepted (or a line it issues by hand) is issued by its
        // desk: Nubi opens the ISSUE work order and registers the policy when it is completed.
        if (r.status === 'WORK_ORDER') {
          await render('ordenes');
          return `${r.message ?? 'Se abrió la orden de emisión.'} La póliza se registra al completarla con su número.`;
        }
        await render('polizas');
        return `Póliza ${r.policyNumber} emitida a nombre de ${policyholder.fullName}.`;
      });
    },
  },
  changeData: {
    uwDoc: (control) => upload(control as unknown as HTMLInputElement),
    uwPhoto: (control) => upload(control as unknown as HTMLInputElement),
  },
};
